import React, { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, FileCheck2, MessageSquareText, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

import ChatArea, { ReportFindingsApplyResult } from '../Chat/ChatArea';
import apiService from '../../services/api';
import type { ChatMessage, CompletionCase, CompletionItem, CompletionTargetedJob } from '../../types';
import { matchCompletionItemsInChat } from '../../utils/reportChat';

export interface ReportChatContext {
  reportId: string;
  runId?: string;
  documentId: number;
  documentName: string;
  companyName: string;
  periodLabel: string;
  suggestions: string[];
}

const MAX_TARGETED_ITEMS = 6;
const JOB_POLL_INTERVAL_MS = 2000;
const JOB_POLL_TIMEOUT_MS = 180_000;

const delay = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const errorDetail = (error: unknown, fallback: string): string => (
  (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback
);

const waitForCompletionJobs = async (
  initialJobs: CompletionTargetedJob[],
): Promise<CompletionTargetedJob[]> => {
  let jobs = initialJobs;
  const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
  while (jobs.some((job) => job.status === 'pending' || job.status === 'running') && Date.now() < deadline) {
    await delay(JOB_POLL_INTERVAL_MS);
    jobs = await Promise.all(jobs.map(async (job) => {
      if (job.status !== 'pending' && job.status !== 'running') return job;
      try {
        return await apiService.getCompletionJob(job.job_id);
      } catch {
        // A transient poll failure must not discard a job that continues on
        // the server. Keep its last known state and try again next interval.
        return job;
      }
    }));
  }
  return jobs;
};

interface ReportChatDrawerProps {
  isOpen: boolean;
  context: ReportChatContext | null;
  onClose: () => void;
}

const ReportChatDrawer: React.FC<ReportChatDrawerProps> = ({ isOpen, context, onClose }) => {
  const queryClient = useQueryClient();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (wasOpenRef.current) {
        window.requestAnimationFrame(() => document.getElementById('report-chat-trigger')?.focus());
        wasOpenRef.current = false;
      }
      return;
    }
    wasOpenRef.current = true;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const applyReportFindings = useCallback(async (message: ChatMessage): Promise<ReportFindingsApplyResult> => {
    if (!context?.runId) throw new Error('ריצת הניתוח עדיין לא מוכנה לעדכון תיק ההשלמה');

    let completionCase: CompletionCase;
    try {
      completionCase = await apiService.getCompletionCaseForRun(context.runId);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) throw new Error(errorDetail(error, 'לא הצלחנו לטעון את תיק ההשלמה'));
      completionCase = await apiService.openCompletionCase(context.runId);
    }

    const items = await apiService.getCompletionItems(completionCase.case_id, true);
    const matchedItems = matchCompletionItemsInChat(message.content, items, MAX_TARGETED_ITEMS);
    if (matchedItems.length === 0) {
      return {
        matched: 0,
        updated: 0,
        review: 0,
        unresolved: 0,
        pending: 0,
        itemNames: [],
      };
    }

    const starts: Array<{
      item: CompletionItem;
      job?: CompletionTargetedJob;
      error?: unknown;
    }> = await Promise.all(matchedItems.map(async (item) => {
      try {
        return {
          item,
          job: await apiService.retryCompletionItem(
            completionCase.case_id,
            item.item_key,
            message.content.slice(0, 20_000),
          ),
        };
      } catch (error) {
        return { item, error };
      }
    }));
    const startedJobs: CompletionTargetedJob[] = [];
    starts.forEach((start) => {
      if (start.job) startedJobs.push(start.job);
    });
    if (startedJobs.length === 0) {
      throw new Error(errorDetail(starts[0]?.error, 'לא הצלחנו להתחיל את האימות מול הדוח'));
    }
    let jobs = startedJobs;
    try {
      jobs = await waitForCompletionJobs(startedJobs);
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['completion-items', completionCase.case_id] }),
        queryClient.invalidateQueries({ queryKey: ['completion-case-run', context.runId] }),
      ]);
    }

    const jobItemKeys = new Map(starts.flatMap((start) => (
      start.job ? [[start.job.job_id, start.item.item_key] as const] : []
    )));
    const failedStartKeys = new Set(starts.flatMap((start) => (start.error ? [start.item.item_key] : [])));
    const pendingJobKeys = new Set(jobs
      .filter((job) => job.status === 'pending' || job.status === 'running')
      .flatMap((job) => job.item_key ? [job.item_key] : [jobItemKeys.get(job.job_id)])
      .filter((itemKey): itemKey is string => Boolean(itemKey)));
    const failedJobKeys = new Set(jobs
      .filter((job) => job.status === 'error')
      .flatMap((job) => job.item_key ? [job.item_key] : [jobItemKeys.get(job.job_id)])
      .filter((itemKey): itemKey is string => Boolean(itemKey)));

    let refreshedItems: CompletionItem[] = [];
    try {
      refreshedItems = await apiService.getCompletionItems(completionCase.case_id);
    } catch {
      // Query invalidation above still refreshes the workbench; job outcomes
      // provide a conservative fallback for the inline summary.
    }
    const refreshedByKey = new Map(refreshedItems.map((item) => [item.item_key, item]));
    let updated = 0;
    let review = 0;
    let unresolved = 0;
    let pending = 0;

    matchedItems.forEach((originalItem) => {
      const item = refreshedByKey.get(originalItem.item_key) || originalItem;
      if (pendingJobKeys.has(item.item_key)) pending += 1;
      else if (failedStartKeys.has(item.item_key) || failedJobKeys.has(item.item_key)) unresolved += 1;
      else if (['verified', 'calculated'].includes(item.evidence_status) || item.resolution_status === 'resolved') updated += 1;
      else if (item.evidence_status === 'candidate' || item.resolution_status === 'manual_review') review += 1;
      else unresolved += 1;
    });

    const result = {
      matched: matchedItems.length,
      updated,
      review,
      unresolved,
      pending,
      itemNames: matchedItems.map((item) => item.name?.trim() || item.item_key),
    };
    if (updated > 0 || review > 0) toast.success('תיק ההשלמה עודכן מהממצאים שאומתו');
    return result;
  }, [context, queryClient]);

  return (
    <>
      <button
        type="button"
        aria-label="סגור את חלונית הצ׳אט"
        tabIndex={isOpen ? 0 : -1}
        onClick={onClose}
        className={`fixed inset-x-0 bottom-0 top-16 z-30 bg-sidebar-950/30 backdrop-blur-[1px] transition-opacity lg:hidden ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <aside
        role="dialog"
        aria-label="צ׳אט על הדוח"
        aria-hidden={!isOpen}
        className={`fixed bottom-0 left-0 top-16 z-40 flex w-full flex-col border-r border-gray-200 bg-white shadow-2xl transition-[transform,visibility] duration-200 ease-out sm:w-[430px] ${isOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'}`}
      >
        <header className="border-b border-gray-200 bg-white px-4 py-3" dir="rtl">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
              <MessageSquareText className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-gray-950">שאל על הדוח</h2>
              {context && (
                <p className="mt-0.5 truncate text-xs text-gray-500" title={`${context.companyName} · ${context.periodLabel}`}>
                  {context.companyName} · {context.periodLabel}
                </p>
              )}
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="סגור"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {context && (
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-medium">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">
                <BookOpenCheck className="h-3.5 w-3.5" aria-hidden="true" />
                ניתוח דוח פעיל
              </span>
              <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-primary-50 px-2 py-1 text-primary-700" title={context.documentName}>
                <FileCheck2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="max-w-52 truncate">מצורף: {context.documentName}</span>
              </span>
            </div>
          )}
        </header>

        <div className="min-h-0 flex-1">
          {context ? (
            <ChatArea
              variant="contextual"
              contextKey={`${context.reportId}:${context.documentId}`}
              contextDocument={{ id: context.documentId, name: context.documentName }}
              initialRecipeId="public_company_report"
              usageCategory="public_reports"
              lockRecipe
              contextualSuggestions={context.suggestions}
              emptyTitle="מה תרצה לבדוק בדוח?"
              emptyDescription="הסוכן מחובר לדוח המלא ולמתכון ניתוח חברות ציבוריות. אפשר גם לבקש השלמה של נתון חסר או לבדוק מידע שלא יוצא לאקסל."
              onApplyReportFindings={context.runId ? applyReportFindings : undefined}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center" dir="rtl">
              <MessageSquareText className="h-9 w-9 text-gray-300" aria-hidden="true" />
              <h2 className="mt-4 text-base font-semibold text-gray-900">פתח דוח כדי לשאול עליו</h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">החלונית תחבר אוטומטית את הדוח ואת מתכון הניתוח לשיחה.</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default ReportChatDrawer;
